import { Store } from '@ngxs/store';
import { defer, Observable } from 'rxjs';
import { map, shareReplay, switchMap } from 'rxjs/operators';

import { Avatar, ignoreNullish, Loadable, Logger, PillColor } from '@aiwyn/shared';
import { DeliverableIdentityType, getSafeSendStatus, getSafeSendStatusColor, isSafeSendDeliverable } from '../utility';
import { TypedIdentity } from '../models';
import { CommonClientRequestsState } from '../state';

import { AbstractBaseCommonClientRequestController } from './abstract-base-common-client-request-controller.model';
import { BaseFile, DeliverableModel } from '@aiwyn/api';

export class BaseCommonDeliverable<
  Deliverable extends DeliverableModel = DeliverableModel,
  DeliverableTypeClass extends CommonDeliverableModelToClassMap<Deliverable> = CommonDeliverableModelToClassMap<Deliverable>,
> extends AbstractBaseCommonClientRequestController<TypedIdentity<DeliverableIdentityType>, Deliverable, DeliverableTypeClass> {

  readonly model$: Observable<Loadable<Deliverable>> = defer(() =>
    this._store.select(CommonClientRequestsState.getDeliverableLoadable(this.identity)),
  ).pipe(
    ignoreNullish(),
  ) as Observable<Loadable<Deliverable>>;

  protected _create$(
    model$: Observable<Loadable<Deliverable>>,
    store: Store,
    logger: Logger,
  ): Observable<DeliverableTypeClass> {
    return model$.pipe(
      ignoreNullish(),
      map(_ => new CommonDeliverablePlaceholder(model$, store, logger) as DeliverableTypeClass),
    );
  }

  id$: Observable<number> = this.data$.pipe(
    map(data => data.id),
  );

  name$: Observable<string> = this.data$.pipe(
    map(data => data.name),
  );

  description$: Observable<string> = this.data$.pipe(
    map(data => data.description),
  );

  files$: Observable<BaseFile[]> = this.data$.pipe(
    map(data => data.fileIds ?? []),
    switchMap(fileIds => this._store.select(CommonClientRequestsState.getFiles(fileIds))),
    shareReplay(1),
  );

  isSafeSendDeliverable$: Observable<boolean> = this.data$.pipe(
    map(data => isSafeSendDeliverable(data)),
    shareReplay(1),
  );

  safeSendStatus$: Observable<string | undefined> = this.data$.pipe(
    map(data => getSafeSendStatus(data.custom_field_values)),
    shareReplay(1),
  );

  safeSendStatusColor$: Observable<PillColor> = this.safeSendStatus$.pipe(
    map(status => getSafeSendStatusColor(status || '')),
    shareReplay(1),
  );

  avatars$: Observable<Avatar[]> = this.data$.pipe(
    map(data => data.assignees ?? []),
    map(assignees =>
      assignees.map(assignee => {
        const fullName = assignee.fullName || '';
        const nameParts = fullName.split(/\s/);
        const firstName = nameParts[0] || 'Unknown';
        const lastName = nameParts.at(-1) || 'User';

        return {
          id: assignee.contactId,
          firstName,
          lastName,
          fullName,
        } as Avatar;
      }),
    ),
    shareReplay(1),
  );
}

abstract class AbstractCommonDeliverable<
  Deliverable extends DeliverableModel = DeliverableModel,
> {
  constructor(
    protected readonly model$: Observable<Loadable<Deliverable>>,
    protected readonly _store: Store,
    protected readonly _logger: Logger,
  ) {}
}

export class CommonDeliverablePlaceholder extends AbstractCommonDeliverable<DeliverableModel> {}

export type CommonDeliverableModelToClassMap<Assignee extends DeliverableModel> =
  Assignee extends DeliverableModel ? CommonDeliverablePlaceholder : never;
